import {useIsLoggedIn} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import {isFinishRegistrationModalVisibleAtom} from "@/store/jotaiAtoms";
import type {TUserState} from "@/store/types";
import {useSetAtom} from "jotai";
import jwtDecode from "jwt-decode";
import {useLocation, useNavigate, useSearchParams} from "react-router";
import {useAsync} from "react-use";

const ROUTES_WITH_ACCESS_TOKEN = ["/welcome", "/verify-email"];

type TJwtToken = {
  exp: number;
  jti: string;
  token_type: "access" | "refresh";
  user_id: number;
};

const useFetchUserFromAuthToken = (callback?: (user: TUserState) => void) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const setIsFinishRegistrationModalVisible = useSetAtom(isFinishRegistrationModalVisibleAtom);
  const isLoggedIn = useIsLoggedIn();

  const {loading, value: accessToken} = useAsync(async () => {
    if (!ROUTES_WITH_ACCESS_TOKEN.find(url => location.pathname.includes(url)) || isLoggedIn) {
      return;
    }

    const accessToken = searchParams.get("access_token");

    if (!accessToken) {
      setIsFinishRegistrationModalVisible(false);
      return;
    }

    try {
      const userId = (jwtDecode(accessToken) as TJwtToken).user_id;
      const response = await client.get(`/users/${userId}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      callback?.(response.data);
      return accessToken;
    } catch (err) {
      setIsFinishRegistrationModalVisible(false);
      navigate("/login/");
      throw err;
    }
  }, [searchParams, navigate, location.pathname, isLoggedIn]);

  return {isLoading: loading, accessToken};
};

export default useFetchUserFromAuthToken;
